import React, { useEffect, useMemo, useRef, useState } from 'react';

// Windowing constants. The row height is enforced inline rather than left to
// CSS, because the scroll maths depends on every row being exactly this tall.
const ROW_HEIGHT = 32;
const LIST_HEIGHT = 7 * ROW_HEIGHT;
// Rows rendered beyond the visible window on each side, so a flick of the wheel
// doesn't show blank space before the next render lands.
const OVERSCAN = 5;

/**
 * EditableSelect — a dropdown you can also type into.
 *
 * Deliberately not a native `<datalist>`. That is the same idea in one line of
 * markup, but it renders no affordance at all: the input looks exactly like a
 * plain text box, and the browser only offers suggestions once you have already
 * guessed enough of the value to type it. A list nobody can see is not a list.
 *
 * So: a visible caret that opens the full set, substring filtering as you type,
 * and — the point of the whole control — the typed value is kept verbatim
 * whether or not it matches an option. Picking from the list guarantees the
 * value matches our data; typing lets genuinely new values through.
 *
 * The list is windowed: with ~1,100 accounts, rendering every match built over
 * a thousand DOM nodes before the dropdown could paint. Only the visible slice
 * is rendered, with spacers standing in for the rest, so the whole list stays
 * scrollable while the DOM holds a couple of dozen rows.
 */
const EditableSelect = ({
  id,
  value,
  onChange,
  options,
  formatSelectedValue = (option) => option.value,
  placeholder,
  loading = false,
  loadingMessage = 'Loading…',
  emptyMessage = 'No suggestions available',
  // Names the caret for screen readers. Worth setting when a form has more than
  // one of these, so the buttons aren't two identical "Show options".
  optionsLabel = 'Show options',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Typing filters; opening with the caret (or with an exact selection already
  // in the box) shows everything, so the list is always browsable.
  const matches = useMemo(() => {
    const term = (value || '').trim().toLowerCase();
    if (!term) return options;
    const exact = options.some(
      (option) => option.value.toLowerCase() === term
    );
    if (exact) return options;
    return options.filter(
      (option) =>
        option.value.toLowerCase().includes(term) ||
        (option.label || '').toLowerCase().includes(term)
    );
  }, [options, value]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );
  const inputValue = selectedOption ? formatSelectedValue(selectedOption) : value;

  // Click-away closes. Listening on the document (not onBlur) keeps a click on
  // an option from racing the blur that would unmount it first.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  // Back to the top whenever the result set changes, so a filtered list never
  // opens scrolled into a region that no longer has rows.
  useEffect(() => {
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [value, open]);

  // Arrow keys can walk past the rendered window, which without this would
  // highlight a row that isn't on screen — or isn't in the DOM at all.
  useEffect(() => {
    const list = listRef.current;
    if (!open || highlighted < 0 || !list) return;
    const top = highlighted * ROW_HEIGHT;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (top + ROW_HEIGHT > list.scrollTop + LIST_HEIGHT) {
      list.scrollTop = top + ROW_HEIGHT - LIST_HEIGHT;
    }
    setScrollTop(list.scrollTop);
  }, [highlighted, open]);

  const windowStart = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const windowEnd = Math.min(
    matches.length,
    Math.ceil((scrollTop + LIST_HEIGHT) / ROW_HEIGHT) + OVERSCAN
  );
  const visible = matches.slice(windowStart, windowEnd);

  const choose = (option) => {
    onChange(option.value);
    setOpen(false);
    setHighlighted(-1);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((prev) => {
        if (matches.length === 0) return -1;
        const next = prev + step;
        if (next < 0) return matches.length - 1;
        if (next >= matches.length) return 0;
        return next;
      });
      return;
    }
    if (event.key === 'Enter' && open && highlighted >= 0 && matches[highlighted]) {
      // Only swallow Enter when it is actually picking something, so the form
      // still submits normally the rest of the time.
      event.preventDefault();
      choose(matches[highlighted]);
    }
  };

  return (
    <div className="position-relative" ref={containerRef}>
      <div className="input-group input-group-sm">
        <input
          id={id}
          type="text"
          className="form-control form-control-sm"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={placeholder}
          value={inputValue}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlighted(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          tabIndex={-1}
          disabled={disabled}
          aria-label={optionsLabel}
          onClick={() => {
            setOpen((wasOpen) => !wasOpen);
            setHighlighted(-1);
          }}
        >
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </button>
      </div>

      {open && (
        <ul
          id={`${id}-listbox`}
          ref={listRef}
          className="list-unstyled bg-body border rounded shadow-sm mb-0 position-absolute w-100"
          role="listbox"
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          style={{ zIndex: 1060, maxHeight: `${LIST_HEIGHT}px`, overflowY: 'auto' }}
        >
          {matches.length === 0 ? (
            <li className="px-2 py-1 small text-muted">
              {/* Three genuinely different situations. Reporting "unavailable"
                  while a fetch is still in flight reads as a broken service. */}
              {loading
                ? loadingMessage
                : options.length === 0
                ? emptyMessage
                : 'No match — it will be added as typed.'}
            </li>
          ) : (
            <>
              {/* Spacers stand in for the rows outside the window, so the
                  scrollbar reflects the whole list rather than the slice. */}
              <li
                style={{ height: windowStart * ROW_HEIGHT }}
                aria-hidden="true"
                data-testid="editable-select-spacer"
              />
              {visible.map((option, offset) => {
                const index = windowStart + offset;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      className={`btn btn-sm w-100 text-start rounded-0 text-truncate ${
                        index === highlighted
                          ? 'btn-primary'
                          : 'btn-link text-body text-decoration-none'
                      }`}
                      style={{ height: ROW_HEIGHT }}
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => choose(option)}
                    >
                      {option.value}
                      {option.label && (
                        <span className={index === highlighted ? 'ms-2' : 'ms-2 text-muted'}>
                          {option.label}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              <li
                style={{ height: (matches.length - windowEnd) * ROW_HEIGHT }}
                aria-hidden="true"
                data-testid="editable-select-spacer"
              />
            </>
          )}
        </ul>
      )}
    </div>
  );
};

export default EditableSelect;
